// src/context/ThemeContext.js - Enhanced with Animations
import React, { createContext, useState, useContext, useEffect } from 'react';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [isDark, setIsDark] = useState(() => {
    const saved = localStorage.getItem('theme');
    if (saved) {
      return saved === 'dark';
    }
    // Auto-detect system preference
    return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  const [isTransitioning, setIsTransitioning] = useState(false);

  useEffect(() => {
    // Apply theme class
    if (isDark) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }

    // Update meta theme-color for mobile browsers
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.setAttribute('content', isDark ? '#111827' : '#ffffff');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'theme-color';
      meta.content = isDark ? '#111827' : '#ffffff';
      document.head.appendChild(meta);
    }
  }, [isDark]);

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = (e) => {
      // Only auto-switch if user hasn't manually set a preference
      const savedTheme = localStorage.getItem('theme');
      if (!savedTheme) {
        setIsDark(e.matches);
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const toggleTheme = () => {
    setIsTransitioning(true);

    // Add transition animation to root
    document.documentElement.style.setProperty('--theme-transition', 'all 0.3s ease-in-out');

    // Create ripple effect from toggle button
    const createRipple = () => {
      const ripple = document.createElement('div');
      ripple.className = 'theme-ripple';
      ripple.style.cssText = `
        position: fixed;
        right: 24px;
        bottom: 96px;
        width: 0;
        height: 0;
        border-radius: 50%;
        background: ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'};
        pointer-events: none;
        z-index: 9999;
        animation: ripple-expand 0.6s ease-out forwards;
      `;

      document.body.appendChild(ripple);

      setTimeout(() => {
        ripple.remove();
      }, 600);
    };

    createRipple();

    // Toggle theme after brief delay for smooth transition
    setTimeout(() => {
      setIsDark(prev => !prev);
      
      setTimeout(() => {
        setIsTransitioning(false);
        document.documentElement.style.removeProperty('--theme-transition');
      }, 300);
    }, 50);

    // Console log with emoji
    console.log(isDark ? '☀️ Switching to Light Mode' : '🌙 Switching to Dark Mode');
  };

  const value = {
    isDark,
    toggleTheme,
    theme: isDark ? 'dark' : 'light',
    isTransitioning
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
      
      {/* Global theme transition styles */}
      <style jsx global>{`
        @keyframes ripple-expand {
          from {
            width: 0;
            height: 0;
            opacity: 1;
          }
          to {
            width: 200vmax;
            height: 200vmax;
            margin-left: -100vmax;
            margin-top: -100vmax;
            opacity: 0;
          }
        }

        /* Smooth transitions for theme changes */
        .dark {
          color-scheme: dark;
        }

        /* Prevent flash of wrong theme */
        html {
          background-color: var(--bg-color, #ffffff);
          transition: background-color 0.3s ease-in-out;
        }

        html.dark {
          --bg-color: #111827;
        }

        /* Smooth color transitions */
        * {
          transition: background-color 0.3s ease-in-out, 
                      border-color 0.3s ease-in-out,
                      color 0.3s ease-in-out;
        }

        /* Exclude animations from transitions */
        *::before,
        *::after {
          transition: background-color 0.3s ease-in-out, 
                      border-color 0.3s ease-in-out,
                      color 0.3s ease-in-out;
        }

        /* Don't transition transform/opacity animations */
        [class*="animate-"],
        [class*="transition-transform"],
        [class*="transition-opacity"] {
          transition: none !important;
        }

        /* Smooth scroll */
        html {
          scroll-behavior: smooth;
        }

        /* Custom scrollbar for dark mode */
        html.dark::-webkit-scrollbar {
          width: 12px;
          background-color: #1f2937;
        }

        html.dark::-webkit-scrollbar-thumb {
          background: linear-gradient(to bottom, #3b82f6, #8b5cf6);
          border-radius: 6px;
        }

        html.dark::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(to bottom, #2563eb, #7c3aed);
        }

        /* Light mode scrollbar */
        html::-webkit-scrollbar {
          width: 12px;
          background-color: #f3f4f6;
        }

        html::-webkit-scrollbar-thumb {
          background: linear-gradient(to bottom, #60a5fa, #a78bfa);
          border-radius: 6px;
        }

        html::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(to bottom, #3b82f6, #8b5cf6);
        }
      `}</style>
    </ThemeContext.Provider>
  );
};

export default ThemeContext;